import { ReactNode, useEffect, useRef, useState } from "react";

import {
    BORDER_RADIUS_ROUNDED,
    Button,
    Column,
    DialogWrapper,
    FormInput,
    IconButton,
    TagTextAlert,
    Text,
    ToggleButtons,
    ToggleOption
} from "@bridgesplit/ui";
import { formatPercent, greaterThan, percentUiToDecimals } from "@bridgesplit/utils";
import { SettingsOutlined } from "@mui/icons-material";
import { COPY } from "app/constants";
import { AppDialog, useAppDialog } from "app/utils/dialog";

import { DialogHeader, DialogHeaderWithBack } from "./dialog";

export const DEFAULT_SLIPPAGE = 0.1;
export const AUTO_SLIPPAGE = "auto";
export const DEFAULT_SLIPPAGE_PRESETS = [DEFAULT_SLIPPAGE, 0.5, 1];
const CUSTOM_SLIPPAGE = -1;

export type SlippageValue = number | typeof AUTO_SLIPPAGE;
export type SlippageDialogProps = Omit<SlippageWrapperProps, "children" | "isEditing" | "close">;

function SlippageInput({
    value,
    setValue,
    presets = DEFAULT_SLIPPAGE_PRESETS,
    showAuto = false
}: {
    value: SlippageValue | undefined;
    setValue: (value: SlippageValue | undefined) => void;
    presets?: readonly SlippageValue[];
    showAuto?: boolean;
}) {
    const inputRef = useRef<HTMLInputElement>(null);

    const formatNumberLabel = (percent: number) =>
        formatPercent(percent, { minimumSignificantDigits: 1, skipMultiplication: true });

    const formatAutoLabel = () => "Auto";

    const createPresetOption = (percent: SlippageValue) => {
        if (typeof percent === "number") {
            return {
                value: percent,
                label: formatNumberLabel(percent)
            };
        }
        return {
            value: percent,
            label: formatAutoLabel()
        };
    };

    const options: ToggleOption<SlippageValue>[] = showAuto
        ? [{ value: AUTO_SLIPPAGE, label: formatAutoLabel() }, ...presets.map(createPresetOption)]
        : presets.map(createPresetOption);

    const isCustom = !presets.some((percent) => percent === value) && typeof value === "number";
    useEffect(() => {
        if (!inputRef.current || !isCustom) return;
        inputRef.current.focus();
    }, [isCustom]);

    options.push({
        value: CUSTOM_SLIPPAGE,
        toggleProps: { disableRipple: true },
        label: isCustom ? (
            <FormInput
                variant="number"
                adornment="%"
                decimals={2}
                InputProps={{ inputRef }}
                validateNumber={(val) => (val ? val < 100 : true)}
                onClick={(e) => e.stopPropagation()}
                fullWidth
                sx={{ "& fieldset": { border: "none" } }}
                focused
                setValue={(val) => setValue(val)}
                placeholder="0"
                value={typeof value === "number" ? value : undefined}
            />
        ) : (
            "Custom"
        )
    });

    return (
        <Column spacing={1}>
            <ToggleButtons
                value={value}
                setValue={(percentSlippage) => {
                    if (percentSlippage === undefined) return;
                    setValue(percentSlippage);
                }}
                options={options}
            />
            {typeof value === "number" && greaterThan(value, 5) && (
                <TagTextAlert color="warning" icon="warning">
                    High slippage may result in an unfavorable trade
                </TagTextAlert>
            )}
        </Column>
    );
}

type SlippageSettingsButtonProps = {
    value: SlippageValue | undefined;
    showSlippageValue?: boolean;
    open: () => void;
};

function SlippageSettingsButton({ value, open, showSlippageValue = true }: SlippageSettingsButtonProps) {
    const formatNumberValue = (val: number) => formatPercent(val, { skipMultiplication: true });
    const formatAutoValue = () => "Auto";

    const displayValue = () => {
        if (value === AUTO_SLIPPAGE) {
            return formatAutoValue();
        }
        if (typeof value === "number") {
            return formatNumberValue(value);
        }
        return "";
    };

    if (!showSlippageValue) {
        return (
            <IconButton
                textVariant="caption"
                textColor="caption"
                sx={{ p: 0.5 }}
                jsxIcon={<SettingsOutlined />}
                onClick={open}
            />
        );
    }

    return (
        <Button
            textProps={{ variant: "body2", svgColor: "disabled" }}
            sx={{ p: 0.5, borderRadius: BORDER_RADIUS_ROUNDED }}
            height={20}
            variant="outlined"
            onClick={open}
        >
            <SettingsOutlined /> {displayValue()}
        </Button>
    );
}

type SlippageWrapperProps = {
    isEditing: boolean;
    close: () => void;
    value: SlippageValue | undefined;
    setValue: (value: SlippageValue | undefined) => void;
    children: ReactNode;
    presets: readonly SlippageValue[];
    showAuto: boolean;
};

export function SlippageSettingsDialog() {
    const { getData, close } = useAppDialog();
    const data = getData(AppDialog.SlippageSettings);
    const [value, setValue] = useState(data?.value);

    return (
        <DialogWrapper onClose={close}>
            <Column spacing={2}>
                <DialogHeader
                    spacing={1}
                    headerVariant="h4"
                    header="Update max slippage"
                    description={COPY.SLIPPAGE_TOOLTIP}
                />
                <SlippageInput value={value} setValue={setValue} presets={data?.presets} showAuto={data?.showAuto} />
            </Column>
            <Button
                variant="contained"
                onClick={() => {
                    data?.setValue(value);
                    close();
                }}
            >
                Confirm max slippage
            </Button>
        </DialogWrapper>
    );
}

export function formatSlippageRowEndText(
    slippagePercentUi: SlippageValue | undefined,
    slippagePercentDecimals: number | undefined
): string {
    const valueText =
        typeof slippagePercentUi === "number" ? formatPercent(slippagePercentDecimals, { customDecimals: 2 }) : "Auto";

    return `Slippage: ${valueText}`;
}

function SlippageWrapper({
    isEditing,
    value: initialValue,
    setValue: setPropsValue,
    children,
    close,
    presets,
    showAuto
}: SlippageWrapperProps) {
    const [value, setValue] = useState(initialValue);

    if (!isEditing) return <>{children}</>;
    return (
        <>
            <Column spacing={2}>
                <DialogHeaderWithBack header="Update max slippage" back={close} leftAlignHeader />
                <Text color="caption">{COPY.SLIPPAGE_TOOLTIP}</Text>
                <SlippageInput value={value} setValue={setValue} presets={presets} showAuto={showAuto} />
            </Column>
            <Button
                variant="contained"
                onClick={() => {
                    setPropsValue(value);
                    close();
                }}
            >
                Update max slippage
            </Button>
        </>
    );
}

function useSlippage(options?: {
    defaultSlippageUi?: SlippageValue;
    presetsUi?: readonly SlippageValue[];
    onEdit?: (value: SlippageValue | undefined) => void;
    showAuto?: boolean;
    closeDialog?: () => void;
}) {
    const [slippageUi, setSlippageUi] = useState<SlippageValue | undefined>(options?.defaultSlippageUi);
    const [isEditing, setIsEditing] = useState(false);

    const getEffectiveSlippage = (value: SlippageValue | undefined): SlippageValue => {
        if (value === undefined) {
            return DEFAULT_SLIPPAGE;
        }
        if (typeof value === "number") {
            return value === CUSTOM_SLIPPAGE ? DEFAULT_SLIPPAGE : value;
        } else {
            return AUTO_SLIPPAGE;
        }
    };

    const slippagePercentUi = getEffectiveSlippage(slippageUi);
    const isAutoSlippage = slippagePercentUi === AUTO_SLIPPAGE;

    const slippageWrapperProps: Omit<SlippageWrapperProps, "children"> = {
        isEditing,
        close: () => setIsEditing(false),
        value: slippagePercentUi,
        setValue: (value) => {
            setSlippageUi(value);
            options?.onEdit?.(value);
        },
        presets: options?.presetsUi ?? DEFAULT_SLIPPAGE_PRESETS,
        showAuto: options?.showAuto ?? false
    };
    const slippageDialogProps: SlippageDialogProps = {
        value: slippagePercentUi,
        setValue: (value) => {
            setSlippageUi(value);
            options?.onEdit?.(value);
        },
        presets: options?.presetsUi ?? DEFAULT_SLIPPAGE_PRESETS,
        showAuto: options?.showAuto ?? false
    };

    const slippageButtonProps: SlippageSettingsButtonProps = {
        value: slippagePercentUi,
        open: () => setIsEditing(true)
    };

    const getSlippageDecimals = (value: SlippageValue) => {
        if (typeof value === "number") {
            return percentUiToDecimals(value);
        }
        return undefined;
    };

    const slippagePercentDecimals = getSlippageDecimals(slippagePercentUi);

    return {
        slippagePercentDecimals,
        slippagePercentUi,
        slippageWrapperProps,
        slippageDialogProps,
        slippageButtonProps,
        isAutoSlippage
    };
}

export const Slippage = {
    Input: SlippageInput,
    Button: SlippageSettingsButton,
    Wrapper: SlippageWrapper,
    useController: useSlippage
};
